import React, { useState, useEffect } from 'react';
import {
  Building2,
  Mail,
  Phone,
  Calendar,
  Save,
  Loader,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { withMask } from 'use-mask-input'
import { CompanyProfile } from '../../types';
import api from '../../services/api';
import { LoadingState } from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';

const CompanySettingsPage: React.FC = () => {
  usePageTitle('Configurações da Empresa');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<CompanyProfile>('/company/profile');
      setProfile(response.data);
      setFormData({
        name: response.data.name,
        email: response.data.email || '',
        phone: response.data.phone || '',
      });
    } catch (err: any) {
      console.error('Error fetching company profile:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados da empresa');
      toast.error('Erro ao carregar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isOwner) {
      toast.error('Apenas o proprietário pode atualizar os dados da empresa');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Nome da empresa é obrigatório');
      return;
    }

    if(formData.phone) {
      formData.phone = formData.phone.replace(/\D/g, '');
    }

    try {
      setSaving(true);
      const response = await api.put('/company/profile', formData);
      setProfile(prev => prev ? { ...prev, ...response.data.company } : null);
      toast.success('Dados da empresa atualizados com sucesso!');
    } catch (err: any) {
      console.error('Error updating company:', err);
      toast.error(err.response?.data?.error || 'Erro ao atualizar dados da empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return <LoadingState message="Carregando dados da empresa..." />;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
          <AlertTriangle size={20} className="text-bordo flex-shrink-0 mt-0.5" />
          <p className="text-sm text-tinta">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
          <AlertTriangle size={20} className="text-bordo flex-shrink-0 mt-0.5" />
          <p className="text-sm text-tinta">Dados da empresa não encontrados</p>
        </div>
      </div>
    );
  }

  const inputBase =
    'w-full bg-white border border-tinta/15 rounded-lg px-4 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo transition-colors disabled:bg-creme-light disabled:cursor-not-allowed disabled:text-tinta-soft';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-black text-tinta leading-tight">
          Configurações da <span className="italic text-bordo">empresa</span>
        </h1>
        <p className="mt-2 text-sm text-tinta-soft">Gerencie as informações da sua empresa</p>
      </div>

      {/* Warning for non-owners */}
      {!isOwner && (
        <div className="flex gap-3 p-4 rounded-lg bg-mostarda/15 border border-mostarda/40">
          <AlertTriangle size={20} className="text-mostarda-dark flex-shrink-0 mt-0.5" />
          <p className="text-sm text-tinta">
            Apenas o proprietário da empresa pode editar estas configurações. Você pode visualizar as informações abaixo.
          </p>
        </div>
      )}

      {/* Company Information Form */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6">
        <h2 className="font-display text-lg font-bold text-tinta mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-bordo" />
          Informações da Empresa
        </h2>

        <form onSubmit={handleSave} className="space-y-4">
          {/* Company Name */}
          <div>
            <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
              Nome da Empresa *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={!isOwner}
              required
              maxLength={255}
              className={inputBase}
              placeholder="Nome da sua empresa"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
              <Mail size={14} className="text-tinta-soft" />
              Email de Contato
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!isOwner}
              className={inputBase}
              placeholder="contato@empresa.com"
            />
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
              <Phone size={14} className="text-tinta-soft" />
              Telefone
            </label>
            <input
              type="tel"
              id="phone"
              ref={withMask('(99) 99999-9999')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isOwner}
              className={inputBase}
              placeholder="(11) 99999-9999"
            />
          </div>

          {/* Save Button */}
          {isOwner && (
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Subscription Info (Read-only) */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6">
        <h2 className="font-display text-lg font-bold text-tinta mb-4 flex items-center gap-2">
          <Calendar size={20} className="text-bordo" />
          Assinatura Atual
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-creme-light border border-tinta/10 rounded-lg p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80 mb-1">Plano</p>
            <p className="font-display text-lg font-bold text-tinta">{profile.plan.name}</p>
          </div>

          <div className="bg-creme-light border border-tinta/10 rounded-lg p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80 mb-1">Status</p>
            <div className="flex items-center gap-2">
              {profile.subscription.status === 'active' ? (
                <>
                  <CheckCircle size={16} className="text-musgo" />
                  <span className="font-display text-lg font-bold text-musgo">Ativo</span>
                </>
              ) : (
                <>
                  <AlertCircle size={16} className="text-mostarda-dark" />
                  <span className="font-display text-lg font-bold text-mostarda-dark capitalize">
                    {profile.subscription.status}
                  </span>
                </>
              )}
            </div>
          </div>

          {profile.subscription.starts_at && (
            <div className="bg-creme-light border border-tinta/10 rounded-lg p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80 mb-1">Início</p>
              <p className="font-display text-lg font-bold text-tinta">
                {formatDate(profile.subscription.starts_at)}
              </p>
            </div>
          )}

          {profile.subscription.ends_at && (
            <div className="bg-creme-light border border-tinta/10 rounded-lg p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80 mb-1">Renovação</p>
              <p className="font-display text-lg font-bold text-tinta">
                {formatDate(profile.subscription.ends_at)}
              </p>
            </div>
          )}
        </div>

        <div className="mt-5 p-3 rounded-lg bg-tinta/5 border border-tinta/10">
          <p className="text-sm text-tinta-soft">
            Para fazer upgrade, downgrade ou cancelar sua assinatura, acesse a{' '}
            <a href="/admin/subscription" className="text-bordo hover:text-bordo-dark font-bold underline">
              página de assinatura
            </a>
            .
          </p>
        </div>
      </div>

      {/* Company Metadata */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6">
        <h2 className="font-display text-lg font-bold text-tinta mb-4">
          Informações do Sistema
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">ID da Empresa:</span>
            <p className="font-mono text-sm text-tinta mt-1">{profile.id}</p>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">Data de Criação:</span>
            <p className="text-sm text-tinta mt-1">{formatDate(profile.created_at)}</p>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">Última Atualização:</span>
            <p className="text-sm text-tinta mt-1">{formatDate(profile.updated_at)}</p>
          </div>

          {profile.subscription.kiwify_email && (
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-tinta-soft/80">Email Kiwify:</span>
              <p className="text-sm text-tinta mt-1">{profile.subscription.kiwify_email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanySettingsPage;
